import { useHandler } from "@redotech/react-util/hook";
import { useOptimizationSettings } from "@redotech/redo-merchant-app-common/support/use-optimization-settings";
import { ShopperAiSettingsOptimizationType } from "@redotech/redo-model/shopper-ai-settings-optimization";
import { toast } from "@redotech/redo-web/alert";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { memo } from "react";
import {
  GenericSettingsOptimizationBanner,
  SettingToOptimize,
} from "../optimization-banners/generic-settings-optimization-banner";
import {
  checkBaseEligibility,
  OptimizationProps,
  SettingsOptimizationDefinition,
} from "../optimization-types";

const DiscountsOptimizationBanner = memo(function DiscountsOptimizationBanner({
  handleComplete,
  handleDismiss,
}: OptimizationProps) {
  const {
    settings,
    mutation,
    handleApplyAll,
    componentLevelDismiss,
    isLoading,
  } = useOptimizationSettings({
    applyAllRecommendations: {
      "concierge/skills/createDiscountCode/enabled": true,
      "concierge/skills/createDiscountCode/maxDiscountPercentage": 10,
      "concierge/skills/createDiscountCode/discountCodeExpirationMinutes": 10_080,
    },
    handleComplete,
    handleDismiss,
    shouldCompleteOnAnyChange: true,
  });

  const handleUpdate = useHandler(async (enabled: boolean) => {
    await mutation.mutateAsync({
      "concierge/skills/createDiscountCode/enabled": enabled,
    });
    toast("Settings applied successfully", { variant: "success" });
  });

  const handleMaxDiscountUpdate = useHandler(async (value: string) => {
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      await mutation.mutateAsync({
        "concierge/skills/createDiscountCode/maxDiscountPercentage": numValue,
      });
    }
  });

  const handleExpirationUpdate = useHandler(async (value: string) => {
    const days = Number(value);
    if (!isNaN(days) && days >= 0) {
      await mutation.mutateAsync({
        "concierge/skills/createDiscountCode/discountCodeExpirationMinutes":
          days * 24 * 60,
      });
    }
  });

  if (isLoading || !settings) {
    return null;
  }

  const maxDiscountPercentage =
    settings["concierge/skills/createDiscountCode/maxDiscountPercentage"] ?? 10;
  const expirationMinutes =
    settings[
      "concierge/skills/createDiscountCode/discountCodeExpirationMinutes"
    ] ?? 10_080;
  const expirationDays = Math.round(expirationMinutes / (24 * 60));

  const settingsConfig: SettingToOptimize[] = [
    {
      title: "Enable discount code creation",
      description:
        "Allow AI to create discount codes when interacting with a customer to increase AOV and improve customer loyalty",
      control: (
        <RedoToggle
          setValue={handleUpdate}
          value={
            settings["concierge/skills/createDiscountCode/enabled"] ?? false
          }
        />
      ),
      onUpdate: () =>
        handleUpdate(
          settings["concierge/skills/createDiscountCode/enabled"] ?? false,
        ),
    },
    {
      title: "Maximum discount percentage",
      description:
        "Maximum discount percentage AI can apply to discount codes (0-100%)",
      control: (
        <RedoTextInput
          inputTypeHint="number"
          max={100}
          min={0}
          placeholder="10"
          setValue={handleMaxDiscountUpdate}
          size="sm"
          step={1}
          value={String(maxDiscountPercentage)}
        />
      ),
      onUpdate: () => handleMaxDiscountUpdate(String(maxDiscountPercentage)),
    },
    {
      title: "Discount code duration",
      description: "How long discount codes remain active (in days)",
      control: (
        <RedoTextInput
          inputTypeHint="number"
          min={0}
          placeholder="7"
          setValue={handleExpirationUpdate}
          size="sm"
          step={1}
          value={String(expirationDays)}
        />
      ),
      onUpdate: () => handleExpirationUpdate(String(expirationDays)),
    },
  ];

  return (
    <GenericSettingsOptimizationBanner
      handleComplete={handleComplete}
      handleDismiss={componentLevelDismiss}
      onApplyAll={handleApplyAll}
      settings={settingsConfig}
    />
  );
});

export const discountsOptimization: SettingsOptimizationDefinition = {
  type: ShopperAiSettingsOptimizationType.DISCOUNTS_OPTIMIZATION,
  priority: 1,
  retries: 2,
  isEligible: async (team, user) => {
    if (!checkBaseEligibility(user)) {
      return false;
    }

    return (
      team.settings.concierge?.enabled === true &&
      team.settings.concierge.skills?.createDiscountCode?.enabled !== true
    );
  },
  component: DiscountsOptimizationBanner,
};
